import { useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { adminQueries, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import {
  applyProblemToForm,
  Button,
  Form,
  FormField,
  FormItem,
  FormMessage,
  FormRootError,
  InputField,
  Label,
  SearchInput,
  Select,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
  ToggleGroup,
} from '@blimo/ui';

const MODES = [
  { value: 'new', label: 'Neue Organisation' },
  { value: 'existing', label: 'Bestehende Organisation' },
];

/** UX validation only – the API validates again (organisation, existence). */
const schema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('new'), organization_name: z.string().trim().min(1, 'Bitte einen Namen angeben.').max(191), party_id: z.string().optional() }),
  z.object({ mode: z.literal('existing'), party_id: z.string().min(1, 'Bitte eine Organisation auswählen.'), organization_name: z.string().optional() }),
]);

/**
 * Mandant anlegen (neue Organisation) oder bestehende Organisation als Mandant kennzeichnen.
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void, onCreated: (client: { id: number }) => void }} props
 */
export function CreateClientSheet({ open, onOpenChange, onCreated }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Mandant anlegen</SheetTitle>
          <SheetDescription>Mandanten sind Organisationen; ihre Mitglieder sehen die zugeordneten Einheiten im Kundenportal.</SheetDescription>
        </SheetHeader>
        {open && <CreateClientForm onDone={() => onOpenChange(false)} onCreated={onCreated} />}
      </SheetContent>
    </Sheet>
  );
}

function CreateClientForm({ onDone, onCreated }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { mode: 'new', organization_name: '', party_id: '' } });
  const mode = useWatch({ control: form.control, name: 'mode' });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const payload = values.mode === 'new' ? { organization_name: values.organization_name.trim() } : { party_id: Number(values.party_id) };
      const client = await api.admin.mandates.create(payload);
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('mandates') });
      toast.success(values.mode === 'new' ? 'Mandant angelegt' : 'Organisation als Mandant gekennzeichnet');
      onDone();
      onCreated(client);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['organization_name', 'party_id'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4 px-4">
        <FormRootError />
        <ToggleGroup label="Art" value={mode} onValueChange={(v) => v && form.setValue('mode', v, { shouldValidate: false })} options={MODES} />
        {mode === 'new' ? (
          <InputField name="organization_name" label="Name der Organisation" autoComplete="organization" />
        ) : (
          <FormField
            name="party_id"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <OrganizationPicker value={field.value} onChange={field.onChange} />
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            {mode === 'new' ? 'Anlegen' : 'Kennzeichnen'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}

/** Organisation search via GET /admin/parties?filter[type]=organization&filter[q]=… (ab 2 Zeichen). */
function OrganizationPicker({ value, onChange }) {
  const api = useApi();
  const id = useId();
  const [q, setQ] = useState('');
  const enabled = q.trim().length >= 2;
  const { data, isFetching } = useQuery({ ...adminQueries.parties(api, { per_page: 20, filter: { q: q.trim(), type: 'organization' } }), enabled });
  const results = enabled ? (data?.data ?? []) : [];

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>Organisation</Label>
      <SearchInput label="Organisation suchen" placeholder="Name suchen …" value={q} onValueChange={setQ} />
      <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} aria-busy={isFetching || undefined}>
        <option value="">{enabled ? 'Bitte auswählen …' : 'Erst suchen (mind. 2 Zeichen)'}</option>
        {results.map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.display_name || `#${p.id}`}
          </option>
        ))}
      </Select>
      {enabled && !isFetching && results.length === 0 && <span className="text-xs text-muted-foreground">Keine Organisation gefunden.</span>}
    </div>
  );
}
